import {useRouter} from 'next/router';
import {pathOr} from 'ramda';
import {useCallback, useEffect, useMemo, useState} from 'react';

import {RESPONSE_STATUSES} from '@/src/app/constants/api';
import SUBSCRIPTION_STATUSES from '@/src/app/constants/subscriptionStatuses';
import useCourseDetailsActions from '@/src/app/store/courseDetails/useCourseDetailsActions';
import useUserActions from '@/src/app/store/user/useUserActions';
import {getAuthorizationToken} from '@/src/app/utils/authorizationToken';
import scrollTopSmoothly from '@/src/app/utils/scrollTopSmoothly';
import useIsMobile from '@/src/hooks/useIsMobile';
import useRoutePaths from '@/src/hooks/useRoutePaths';

import getCourseDetails from '../../courseDetails/utils/getCourseDetails';
import MainContainer from '../../main/container/MainContainer';
import NoAccess from '../../noAccess/containers/NoAccess';
import {LoadingWrapper, SectionContainer} from '../../ui';
import WatchCourseLayout from '../components/WatchCourseLayout';
import css from '../styles/WatchCourseLayout.module.css';
import requestSaveVideoStopTime from '../utils/requestSaveVideoStopTime';
import requestTopicAttachmentSetAsWatched from '../utils/requestTopicAttachmentSetAsWatched';

export const ATTACHMENT_TYPES = {
  video: 'video',
  link: 'link_attachment',
  file: 'pdf_attachment',
};

const WatchCourse = () => {
  const {
    course,
    setCourseDetails,
    contentCreator,
    updateStoppageTime,
    setWritingReviewNotAllowed,
    handleOnVideoWatched,
  } = useCourseDetailsActions();

  const {NOT_FOUND_ROUTE} = useRoutePaths();
  const {user, isLoading: isUserLoading} = useUserActions();
  const {isMaxWidth600} = useIsMobile();
  const {permissions} = user;
  const [isLoading, setLoading] = useState(true);
  const [currentTopic, setCurrentTopic] = useState<any>(null);
  const [currentVideo, setCurrentVideo] = useState(null);
  const [allVideosList, setAllVideosList] = useState([]);
  const [videoDisablesButtons, setVideoDisabledButtons] = useState({
    next: false,
    previous: false,
  });

  const subscriptionStatus = pathOr('', ['subscription', 'status'], user);
  const isUserAuthorized = getAuthorizationToken();
  const {
    query: {slug, ...search},
    push,
  } = useRouter();

  const initNewTopicAndVideo = (videoId: number, postId?: number) => {
    if (postId) {
      const topic =
        course.topics.find((topic) => topic.id === postId) || course.topics[0];

      setCurrentTopic(topic);
      setCurrentVideo(pathOr(null, ['videos', 0], topic));
      return;
    } else {
      let currentTopic = course?.topics[0];
      let currentVideo = course?.topics[0]?.videos[0];

      course.topics.forEach((topic) => {
        topic.videos.forEach((video) => {
          if (video.id === videoId) {
            currentVideo = video;
            currentTopic = topic;
          }
        });
      });

      setCurrentTopic(currentTopic);
      setCurrentVideo(currentVideo);
    }
  };

  useEffect(() => {
    if (isMaxWidth600) {
      scrollTopSmoothly();
    }

    if (!currentVideo || !allVideosList.length) return;

    if (currentVideo.id === allVideosList[allVideosList.length - 1].videoId) {
      return setVideoDisabledButtons({
        next: true,
        previous: false,
      });
    }

    if (currentVideo.id === allVideosList[0].videoId) {
      return setVideoDisabledButtons({
        next: false,
        previous: true,
      });
    }

    setVideoDisabledButtons({
      next: false,
      previous: false,
    });
  }, [currentVideo]);

  useEffect(() => {
    const isAnySearch =
      Object.prototype.hasOwnProperty.call(search, 'video_id') ||
      Object.prototype.hasOwnProperty.call(search, 'postId');
    if (course?.topics?.length && isAnySearch) {
      initNewTopicAndVideo(Number(search.video_id), Number(search.postId));
    }

    if (course?.topics?.length && !isAnySearch) {
      setCurrentTopic(course?.topics[0]);
      setCurrentVideo(course?.topics[0]?.videos[0]);
    }

    if (course?.topics?.length) {
      const allVideosWithTopics = course.topics.reduce(
        (previousTopic, nextTopic) => {
          return [
            ...previousTopic,
            ...nextTopic.videos.map((video) => ({
              videoId: video.id,
              topicId: nextTopic.id,
            })),
          ];
        },
        []
      );
      setAllVideosList(allVideosWithTopics);
    }
  }, [course.id]);

  useEffect(() => {
    requestGetCourse();
  }, [slug]);

  const handleOnTopicClick = useCallback(
    (topicId) => {
      const topic = course.topics.find((topic) => topic.id === topicId);
      setCurrentVideo(topic.videos[0] ?? null);
      setCurrentTopic(topic);
    },
    [course]
  );

  const handleOnTopicVideoClick = useCallback(
    (video, topicId) => {
      setCurrentVideo(video);

      if (!topicId || currentTopic?.id === topicId) {
        return;
      }

      const topic = course.topics.find((topic) => topic.id === topicId);
      if (topic) {
        setCurrentTopic(topic);
      }
    },
    [course, currentTopic?.id]
  );

  const requestGetCourse = useCallback(async () => {
    setLoading(true);
    // @ts-ignore
    const {status, responseBody} = await getCourseDetails(slug);

    if (status === RESPONSE_STATUSES.SUCCESS) {
      !responseBody.data.length
        ? push(NOT_FOUND_ROUTE.getLink())
        : setCourseDetails(responseBody);

      setLoading(false);
    } else {
      push(NOT_FOUND_ROUTE.getLink());
    }
  }, [slug]);

  const handleTopicAttachmentClick = useCallback(
    async (id, type) => {
      if (id && isUserAuthorized) {
        handleOnVideoWatched();
        await requestTopicAttachmentSetAsWatched(id, type);
      }
    },
    [requestTopicAttachmentSetAsWatched, isUserAuthorized]
  );

  const handleOnStoppedWatch = useCallback(
    async (videoId, stoppedTime) => {
      if (videoId && isUserAuthorized) {
        updateStoppageTime(videoId, stoppedTime);
        return await requestSaveVideoStopTime(videoId, stoppedTime);
      }
    },
    [updateStoppageTime, requestSaveVideoStopTime, isUserAuthorized]
  );

  const handleNextVideoClick = useCallback(() => {
    const currentIndex = allVideosList.findIndex(
      (element) => element.videoId === currentVideo.id
    );
    const element = allVideosList[currentIndex + 1];

    if (element) {
      initNewTopicAndVideo(element.videoId);
    }
  }, [allVideosList, currentVideo, course]);

  const handlePreviousVideoClick = useCallback(() => {
    const currentIndex = allVideosList.findIndex(
      (element) => element.videoId === currentVideo.id
    );
    const element = allVideosList[currentIndex - 1];

    if (element) {
      initNewTopicAndVideo(element.videoId);
    }
  }, [allVideosList, currentVideo, course]);

  const isLoadingContent = useMemo(
    () => isLoading || isUserLoading || (isUserAuthorized && !user.user.id),
    [isLoading, isUserLoading, isUserAuthorized, user?.user?.id]
  );

  return (
    <MainContainer
      widthContainer={MainContainer.CONTAINER_WIDTH.FULL_WIDTH}
      topOffset={MainContainer.TOP_OFFSET.OFFSET_0}
    >
      <LoadingWrapper isLoading={isLoadingContent} hideContentBeforeLoad>
        {permissions?.accessWatchCourse || course.isPublic ? (
          <WatchCourseLayout
            onVideoWathced={handleOnVideoWatched}
            courseId={course.courseId}
            translationId={course.id}
            updateDate={course.updateDate}
            currentVideo={currentVideo}
            topics={pathOr([], ['topics'], course)}
            currentTopic={currentTopic}
            onTopicClick={handleOnTopicClick}
            onTopicVideoClick={handleOnTopicVideoClick}
            contentCreator={contentCreator}
            isWritingReviewAllowed={
              course.isWritingReviewAllowed &&
              !course.isHasComment &&
              !(user?.user?.id && contentCreator?.id === user?.user?.id)
            }
            onTopicAttachmentClick={handleTopicAttachmentClick}
            onStoppedWatchVideo={handleOnStoppedWatch}
            onPreviousVideoClick={handlePreviousVideoClick}
            onNextVideoClick={handleNextVideoClick}
            isPreviousDisable={
              videoDisablesButtons.previous || allVideosList.length === 1
            }
            isNextDisable={
              videoDisablesButtons.next || allVideosList.length === 1
            }
            toggleLeaveReviewAllowed={setWritingReviewNotAllowed}
            course={course}
          />
        ) : (
          <SectionContainer>
            <div className={css.noAccessOffset}>
              <NoAccess
                isRenewSubscription={
                  subscriptionStatus === SUBSCRIPTION_STATUSES.EXPIRED
                }
              />
            </div>
          </SectionContainer>
        )}
      </LoadingWrapper>
    </MainContainer>
  );
};

export default WatchCourse;
